import { __read, __assign } from 'tslib';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import { UitkDay } from './uitk-day.esm.js';
import WeekdayNames from './uitk-weekday-names.esm.js';
import dateUtils from './calendar-utils/dateUtils.esm.js';
import { getMonthTitle } from './calendar-utils/intl.esm.js';
import { getSelectionType } from './calendar-utils/selectionUtils.esm.js';
import { isSameDate } from '../generic-utils/dateUtils.esm.js';
import styles from '../uitk-calendar.css';

var getClassNameList = classNames.bind(styles);
var MonthTitle = function (_a) {
    var title = _a.title, monthLabelAlignment = _a.monthLabelAlignment;
    var labelClasses = getClassNameList("uitk-align-".concat(monthLabelAlignment), 'uitk-month-label');
    return title ? React.createElement("span", { className: labelClasses }, title) : null;
};
var isCurrentMonthAndYear = function (month, year) {
    var today = new Date();
    return month === today.getMonth() && year === today.getFullYear();
};
var UitkMonth = function (props) {
    var customAriaLabels = props.customAriaLabels, dayData = props.dayData, dayNameLength = props.dayNameLength, dayTemplateComponent = props.dayTemplateComponent, enablePastDates = props.enablePastDates, enableSameDayDateRange = props.enableSameDayDateRange, focusedDay = props.focusedDay, handleArrowKeyPress = props.handleArrowKeyPress, index = props.index, isFirstMonth = props.isFirstMonth, isLastMonth = props.isLastMonth, isSecondMonth = props.isSecondMonth, isSingleDateSelection = props.isSingleDateSelection, l10n = props.l10n, locale = props.locale, minDayCellHeight = props.minDayCellHeight, _a = props.monthData, year = _a.year, month = _a.month, weeks = _a.weeks, monthLabelAlignment = props.monthLabelAlignment, numColumns = props.numColumns, numMonthsToDisplay = props.numMonthsToDisplay, onDayClick = props.onDayClick, onDayHover = props.onDayHover, onDisabledDayClick = props.onDisabledDayClick, onScrollHandler = props.onScrollHandler, preventScrollOnDayFocus = props.preventScrollOnDayFocus, scrollToMonthRef = props.scrollToMonthRef, selectable = props.selectable, selectionEnd = props.selectionEnd, selectionStart = props.selectionStart, firstMonthCallback = props.firstMonthCallback, lastMonthCallback = props.lastMonthCallback, secondMonthCallback = props.secondMonthCallback, shouldScrollIntoView = props.shouldScrollIntoView, showMonthLabels = props.showMonthLabels, _b = props.stretchy, stretchy = _b === void 0 ? false : _b, unconfirmedSelectionEnd = props.unconfirmedSelectionEnd;
    /**
     * Determines whether a day should be focusable. If there is not a day selected then a focusable day is either
     * the current day of the present month or the first day of the month as long as it is not in the past
     *
     * @param day {object} The custom day object to check
     * @param i {number} index of month
     * @returns {boolean} true if day is the focusable day of the given month, false otherwise
     */
    var firstFocusableDay = function (day, i, isCurrentMonth, tableRow) {
        if (!day) {
            return false;
        }
        var dayMonth = day.month, dayYear = day.year, isToday = day.isToday, isPast = day.isPast, isPastMaxDate = day.isPastMaxDate;
        var startDate = selectionStart ? dateUtils.getDateFromString(selectionStart) : null;
        var outOfRange = true;
        var monthDiff;
        var yearDiff;
        if (numMonthsToDisplay && startDate !== null) {
            yearDiff = dayYear - startDate.getFullYear();
            monthDiff = Math.abs(dayMonth + yearDiff * 12 - startDate.getMonth());
            outOfRange = monthDiff >= numMonthsToDisplay;
            if (startDate.getMonth() < dayMonth && startDate.getFullYear() <= dayYear) {
                outOfRange = true;
            }
        }
        if (outOfRange) {
            if (!isPast && !isPastMaxDate && (i === 0 || isCurrentMonth)) {
                // Focusable day must be within the current date's month
                if (isToday) {
                    return true;
                }
                if (dateUtils.isFirstDayInMonth(day) && tableRow === 0) {
                    // Verifying the table row is 0 prevents potential sibling months from also receiving focus on their first day
                    return true;
                }
            }
        }
        return false;
    };
    var flexBasis = "".concat(100 / numColumns, "%");
    var monthClasses = getClassNameList({
        'uitk-month': true,
        'uitk-month-single': numColumns === 1,
        'uitk-month-double': numColumns === 2,
        'uitk-month-double-left': numColumns === 2 && index % 2 === 0,
        'uitk-month-double-right': numColumns === 2 && index % 2 === 1,
        'uitk-month-multi': numColumns > 2,
        'uitk-month-stretchy': stretchy
    });
    var tableClasses = getClassNameList({
        'uitk-month-table': true,
        'uitk-month-table-stretchy': stretchy
    });
    var monthTitle = getMonthTitle(year, month, locale, 'long');
    var defaultMonthRef = React.useRef(null);
    var monthRef = shouldScrollIntoView ? scrollToMonthRef : defaultMonthRef;
    var intersectionObserverRef = React.useRef(null);
    React.useEffect(function () {
        if (monthRef === null || monthRef === void 0 ? void 0 : monthRef.current) {
            if (isFirstMonth) {
                firstMonthCallback === null || firstMonthCallback === void 0 ? void 0 : firstMonthCallback(monthRef.current);
            }
            if (isSecondMonth) {
                secondMonthCallback === null || secondMonthCallback === void 0 ? void 0 : secondMonthCallback(monthRef.current);
            }
            if (isLastMonth) {
                lastMonthCallback === null || lastMonthCallback === void 0 ? void 0 : lastMonthCallback(monthRef.current);
            }
        }
    }, [isFirstMonth, isLastMonth, isSecondMonth, monthRef, firstMonthCallback, lastMonthCallback, secondMonthCallback]);
    var firstDateInMonth = React.useMemo(function () { return new Date(year, month, 1, 12); }, [year, month]);
    var today = React.useMemo(function () { return new Date(); }, []);
    var isTodayInMonth = React.useMemo(function () { return today.getFullYear() === year && today.getMonth() === month; }, [today, month, year]);
    var date = React.useMemo(function () { return (isTodayInMonth ? today : firstDateInMonth); }, [firstDateInMonth, isTodayInMonth, today]);
    React.useEffect(function () {
        if (!onScrollHandler)
            return;
        if (!intersectionObserverRef.current) {
            intersectionObserverRef.current = new IntersectionObserver(function (_a) {
                var _b = __read(_a, 1), entry = _b[0];
                if (entry.intersectionRatio === 1) {
                    onScrollHandler(date);
                }
            }, { root: null, rootMargin: '0px', threshold: 1 });
        }
        if (defaultMonthRef.current && intersectionObserverRef.current) {
            intersectionObserverRef.current.observe(defaultMonthRef.current);
        }
        return function () {
            if (intersectionObserverRef.current) {
                intersectionObserverRef.current.disconnect();
                intersectionObserverRef.current = null;
            }
        };
    }, [onScrollHandler, date]);
    var isCurrentMonthYear = React.useMemo(function () { return isCurrentMonthAndYear(month, year); }, [month, year]);
    return (React.createElement("div", { style: { flexBasis: "".concat(flexBasis) }, className: monthClasses, ref: monthRef },
        showMonthLabels && React.createElement(MonthTitle, __assign({}, { title: monthTitle, monthLabelAlignment: monthLabelAlignment })),
        React.createElement("table", { "aria-label": monthTitle, className: tableClasses, "data-stid": "month-table", role: "presentation" },
            React.createElement("thead", null,
                React.createElement(WeekdayNames, { dayNameLength: dayNameLength, locale: locale })),
            React.createElement("tbody", { className: getClassNameList('uitk-day-wrap') }, weeks.map(function (days, weekIndex) { return (React.createElement("tr", { key: weekIndex }, days.map(function (day, weekdayIndex) {
                var selectionType = getSelectionType({
                    selectionStart: selectionStart,
                    selectionEnd: selectionEnd,
                    isSingleDateSelection: isSingleDateSelection,
                    day: day,
                    enableSameDayDateRange: enableSameDayDateRange,
                    unconfirmedSelectionEnd: unconfirmedSelectionEnd
                });
                var dayProps = {
                    customAriaLabels: customAriaLabels,
                    day: day,
                    handleArrowKeyPress: handleArrowKeyPress,
                    selectable: selectable,
                    onDayClick: onDayClick,
                    stretchy: stretchy,
                    minHeight: minDayCellHeight,
                    onDayHover: onDayHover,
                    dayData: (day && (dayData === null || dayData === void 0 ? void 0 : dayData[day.dateString])) || (dayData === null || dayData === void 0 ? void 0 : dayData.defaults) || null,
                    dayTemplateComponent: dayTemplateComponent,
                    enablePastDates: enablePastDates !== null && enablePastDates !== void 0 ? enablePastDates : false,
                    onDisabledDayClick: onDisabledDayClick,
                    isFocusable: firstFocusableDay(day, index, isCurrentMonthYear, weekIndex),
                    isFocused: !!(focusedDay && isSameDate(day.dateString, focusedDay.dateString)),
                    l10n: l10n,
                    preventScrollOnDayFocus: preventScrollOnDayFocus,
                    selectionType: selectionType
                };
                return React.createElement(UitkDay, __assign({ key: "day-".concat(weekdayIndex) }, dayProps));
            }))); })))));
};
UitkMonth.displayName = 'UitkMonth';
var dayDataDefault = {
    defaults: {}
};
UitkMonth.defaultProps = {
    dayData: dayDataDefault,
    padWeeks: false,
    scrollToMonth: false,
    selectionEnd: null,
    selectionStart: null
};
// This code is extracted from shouldComponentUpdate and needs to be revisited
var isObject = function (obj) { return obj === Object(obj); };
var getMonth = function (dateString) {
    // Concatenating the year and month 201611, for comparisons
    if (dateString) {
        var parts = dateString.split('-');
        return Number("".concat(parts[0]).concat(parts[1]));
    }
    return null;
};
var zeroFill = function (value) {
    var oneBasedValue = value + 1;
    return oneBasedValue < 10 ? "0".concat(oneBasedValue) : "".concat(oneBasedValue);
};
var getRenderingMonth = function (year, month) {
    var zeroFilledMonth = zeroFill(month);
    return Number("".concat(year).concat(zeroFilledMonth));
};
var shouldComponentUpdateSingle = function (prevProps, nextProps) {
    var _a = prevProps.monthData, month = _a.month, year = _a.year, selectionStart = prevProps.selectionStart;
    var renderingMonth = getRenderingMonth(year, month);
    var selectedMonth = getMonth(nextProps.selectionStart);
    var prevSelectedMonth = getMonth(selectionStart);
    var buffer = nextProps.showSiblingMonths ? 1 : 0;
    // Clear any selections (i.e. render any month with a previous selection)
    if (renderingMonth === prevSelectedMonth) {
        return true;
    }
    // Skip any months outside of the new selection, plus a buffer (if applicable)
    // Using an extra buffer because the early dates from the next month may show in
    // the previous month. Same for next month dates (showSiblingMonths prop)
    if (selectedMonth && (renderingMonth < selectedMonth - buffer || renderingMonth > selectedMonth + buffer)) {
        return false;
    }
    return true;
};
var shouldComponentUpdateRange = function (prevProps, nextProps) {
    var _a = prevProps.monthData, month = _a.month, year = _a.year, selectionStart = prevProps.selectionStart, selectionEnd = prevProps.selectionEnd, unconfirmedSelectionEnd = prevProps.unconfirmedSelectionEnd;
    var renderingMonth = getRenderingMonth(year, month);
    var selectionStartMonth = getMonth(nextProps.selectionStart);
    var selectionEndMonth = getMonth(nextProps.selectionEnd || nextProps.unconfirmedSelectionEnd);
    var outMonth = selectionEndMonth || selectionStartMonth;
    var buffer = nextProps.showSiblingMonths ? 1 : 0;
    // If the new selection, or partial selection contains the month in question, render.
    // Using an extra buffer because the early dates from the next month may show in
    // the previous month. Same for next month dates (showSiblingMonths prop)
    if (renderingMonth >= Number(selectionStartMonth) - buffer && renderingMonth <= Number(outMonth) + buffer) {
        return true;
    }
    var prevSelectionStartMonth = getMonth(selectionStart);
    var prevSelectionEndMonth = getMonth(selectionEnd || (unconfirmedSelectionEnd === nextProps.selectionStart ? null : unconfirmedSelectionEnd));
    var outMonth2 = prevSelectionEndMonth || Number(prevSelectionStartMonth);
    // If an old selection was drawn on this month, rerender to clear
    if (renderingMonth >= Number(prevSelectionStartMonth) && renderingMonth <= outMonth2) {
        return true;
    }
    return false;
};
// The optimizations in shouldComponentUpdate are for improving the responsiveness
// when making selections, particularly when many months are rendered.
var areEqualProps = function (prevProps, nextProps) {
    var propChange = [
        'monthData',
        'showMonthLabels',
        'monthLabelAlignment',
        'padWeeks',
        'showSiblingMonths',
        'selectable',
        'dayData',
        'isSingleDateSelection',
        'locale',
        'index',
        'focusedDay',
        'shouldScrollIntoView',
        'unconfirmedSelectionEnd'
    ].some(function (prop) {
        var thisProp = prevProps[prop];
        var nextProp = nextProps[prop];
        if (isObject(thisProp) && isObject(nextProp)) {
            return JSON.stringify(thisProp) !== JSON.stringify(nextProp);
        }
        return thisProp !== nextProp;
    });
    // Always update if any of the above properties or state change
    if (propChange) {
        return false;
    }
    if (nextProps.isSingleDateSelection) {
        return !shouldComponentUpdateSingle(prevProps, nextProps);
    }
    return !shouldComponentUpdateRange(prevProps, nextProps);
};
var UitkMonth$1 = React.memo(UitkMonth, areEqualProps);

export { areEqualProps, UitkMonth$1 as default, getMonth, getRenderingMonth, isObject, shouldComponentUpdateRange, shouldComponentUpdateSingle, zeroFill };
